/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Calendar, X, ChevronRight, AlertTriangle, CalendarCheck, CalendarX } from 'lucide-react';
import { Link } from 'react-router-dom';
import logger from '@/lib/logger';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GestioneDottori() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availability, setAvailability] = useState(null); // { days, doctors: {id: {slots,next_date,next_time}}, active_without_slots }
  const [onlyWithoutSlots, setOnlyWithoutSlots] = useState(false);
  // Exceptions management
  const [exceptionsDialogOpen, setExceptionsDialogOpen] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({ date: '', reason: '', type: 'holiday', start_time: '09:00', end_time: '13:00' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/doctors?active_only=false`);
      axios.get(`${API}/doctors/availability-summary`).then(r => setAvailability(r.data)).catch(() => {});
      setDoctors(res.data);
    } catch (err) { logger.error('Fetch error:', err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/doctors/${id}`, {});
      toast.success('Dottore disattivato');
      fetchData();
    } catch { toast.error('Errore'); }
  };

  // Exceptions
  const openExceptions = async (doctorId) => {
    setSelectedDoctorId(doctorId);
    try {
      const res = await axios.get(`${API}/doctors/${doctorId}/exceptions`, {});
      setExceptions(res.data || []);
    } catch { setExceptions([]); }
    setNewException({ date: '', reason: '', type: 'holiday', start_time: '09:00', end_time: '13:00' });
    setExceptionsDialogOpen(true);
  };

  const addException = () => {
    if (!newException.date) { toast.error('Seleziona una data'); return; }
    setExceptions(prev => [...prev, { ...newException }]);
    setNewException({ date: '', reason: '', type: 'holiday', start_time: '09:00', end_time: '13:00' });
  };

  const removeException = (index) => {
    setExceptions(prev => prev.filter((_, i) => i !== index));
  };

  const saveExceptions = async () => {
    try {
      await axios.put(`${API}/doctors/${selectedDoctorId}/exceptions`, exceptions, {});
      toast.success('Eccezioni salvate');
      setExceptionsDialogOpen(false);
    } catch { toast.error('Errore nel salvataggio'); }
  };

  const availOf = (id) => availability?.doctors?.[id];
  const hasNoSlots = (d) => d.active && availability && (availOf(d.id)?.slots || 0) === 0;
  const withoutSlotsCount = availability?.active_without_slots || 0;
  const visibleDoctors = onlyWithoutSlots ? doctors.filter(hasNoSlots) : doctors;
  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <PageTitle title="Gestione Dottori" info="Clicca su un dottore per aprire la scheda completa. La colonna Disponibilità segnala i medici senza slot prenotabili nei prossimi 30 giorni." />
        </div>
        {user?.role === 'admin' && (
          <Button onClick={() => navigate('/backoffice/dottori/nuovo')} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="backoffice-doctors-create-button">
            <Plus size={16} className="mr-1" /> Nuovo Dottore
          </Button>
        )}
      </div>

      {availability && withoutSlotsCount > 0 && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="doctors-no-slots-banner">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {withoutSlotsCount === 1 ? '1 medico attivo non ha' : `${withoutSlotsCount} medici attivi non hanno`} disponibilità prenotabile nei prossimi {availability.days} giorni
            </p>
            <p className="text-xs text-amber-800 mt-0.5">I pazienti (e l'assistente AI) non possono prenotare con loro finché non vengono generate le disponibilità.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button size="sm" variant={onlyWithoutSlots ? 'default' : 'outline'} className={onlyWithoutSlots ? 'bg-amber-600 hover:bg-amber-700 text-white border-amber-600' : 'border-amber-400 text-amber-900 hover:bg-amber-100'} onClick={() => setOnlyWithoutSlots(v => !v)} data-testid="doctors-filter-no-slots">
              {onlyWithoutSlots ? 'Mostra tutti' : 'Mostra solo questi'}
            </Button>
            <Link to="/backoffice/disponibilita">
              <Button size="sm" className="bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid="doctors-go-availability"><Calendar size={14} className="mr-1" /> Genera disponibilità</Button>
            </Link>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 rounded" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Specializzazione</TableHead>
                    <TableHead>Servizi</TableHead>
                    <TableHead>Disponibilità</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleDoctors.map(d => (
                    <TableRow
                      key={d.id}
                      className="cursor-pointer hover:bg-[hsl(var(--secondary))] transition-colors duration-150"
                      onClick={() => navigate(`/backoffice/dottori/${d.id}`)}
                      title="Apri la scheda del dottore"
                      data-testid={`doctor-row-${d.id}`}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          Dott. {d.nome} {d.cognome}
                          <ChevronRight size={14} className="text-muted-foreground" />
                        </span>
                      </TableCell>
                      <TableCell>{d.specializzazione}</TableCell>
                      <TableCell><span className="text-sm text-muted-foreground">{d.tariffe?.length || d.servizi_ids?.length || 0} servizi</span></TableCell>
                      <TableCell data-testid={`doctor-availability-${d.id}`}>
                        {!availability ? (
                          <Skeleton className="h-5 w-24 rounded" />
                        ) : !d.active ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (availOf(d.id)?.slots || 0) === 0 ? (
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border border-amber-300 gap-1" title={`Nessuno slot libero nei prossimi ${availability.days} giorni`}>
                            <CalendarX size={12} /> Nessuna disponibilità
                          </Badge>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-sm text-[#1F2A37]" title={`${availOf(d.id).slots} slot liberi nei prossimi ${availability.days} giorni`}>
                            <CalendarCheck size={14} className="text-[#238A92]" />
                            <span className="capitalize">{fmtDate(availOf(d.id).next_date)}</span>
                            <span className="text-muted-foreground text-xs">· {availOf(d.id).slots} slot</span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell><Badge variant={d.active ? 'default' : 'secondary'} className={d.active ? 'bg-green-100 text-green-800' : ''}>{d.active ? 'Attivo' : 'Inattivo'}</Badge></TableCell>
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => openExceptions(d.id)} title="Eccezioni/Ferie">
                            <Calendar size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => navigate(`/backoffice/dottori/${d.id}`)} title="Modifica">
                            <Edit size={14} />
                          </Button>
                          {d.active && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 px-2 text-red-600" title="Disattiva"><Trash2 size={14} /></Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Disattiva Dottore</AlertDialogTitle>
                                  <AlertDialogDescription>Dott. {d.nome} {d.cognome} non sarà più visibile sul sito e non sarà possibile prenotare. Lo storico degli appuntamenti verrà mantenuto. Potrai riattivarlo in qualsiasi momento.</AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDelete(d.id)} className="bg-red-600">Disattiva</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                          {!d.active && (
                            <Button size="sm" variant="ghost" className="h-8 px-2 text-green-600" title="Riattiva"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/doctors/${d.id}`, { active: true });
                                  toast.success(`Dott. ${d.nome} ${d.cognome} riattivato`);
                                  fetchData();
                                } catch { toast.error('Errore'); }
                              }}
                            >Riattiva</Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exceptions Dialog */}
      <Dialog open={exceptionsDialogOpen} onOpenChange={setExceptionsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Gestione Eccezioni / Ferie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Aggiungi giorni di ferie o orari modificati per questo dottore.</p>

            {/* Add new exception */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Data</Label>
                    <Input type="date" value={newException.date} onChange={e => setNewException(p => ({...p, date: e.target.value}))} className="h-8" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Tipo</Label>
                    <select
                      value={newException.type}
                      onChange={e => setNewException(p => ({...p, type: e.target.value}))}
                      className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    >
                      <option value="holiday">Ferie (giorno intero)</option>
                      <option value="modified">Orario modificato</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Motivo</Label>
                  <Input value={newException.reason} onChange={e => setNewException(p => ({...p, reason: e.target.value}))} placeholder="es. Ferie, Congresso..." className="h-8" />
                </div>
                {newException.type === 'modified' && (
                  <div className="flex items-center gap-2">
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs">Dalle</Label>
                      <Input type="time" value={newException.start_time} onChange={e => setNewException(p => ({...p, start_time: e.target.value}))} className="h-8" />
                    </div>
                    <div className="space-y-1 flex-1">
                      <Label className="text-xs">Alle</Label>
                      <Input type="time" value={newException.end_time} onChange={e => setNewException(p => ({...p, end_time: e.target.value}))} className="h-8" />
                    </div>
                  </div>
                )}
                <Button size="sm" onClick={addException} className="bg-[#238A92] hover:bg-[#0F5E66] text-white w-full">
                  <Plus size={14} className="mr-1" /> Aggiungi Eccezione
                </Button>
              </CardContent>
            </Card>

            {/* Existing exceptions */}
            {exceptions.length > 0 ? (
              <div className="space-y-2">
                <Label>Eccezioni configurate</Label>
                {exceptions.map((exc, i) => (
                  <div key={exc.date + exc.type + i} className="flex items-center justify-between border rounded-lg p-3">
                    <div>
                      <p className="text-sm font-medium">{exc.date}</p>
                      <p className="text-xs text-muted-foreground">
                        {exc.type === 'holiday' ? 'Ferie - Giorno intero' : `Orario modificato: ${exc.start_time}-${exc.end_time}`}
                        {exc.reason && ` (${exc.reason})`}
                      </p>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-600 h-8 px-2" onClick={() => removeException(i)}>
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Nessuna eccezione configurata</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExceptionsDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveExceptions} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">Salva Eccezioni</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
